import { useSeo } from '../lib/seo';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildEventSchema } from '../lib/eventSchema';import { Search, X } from 'lucide-react';
import api from '../lib/api';
import EventCard from '../components/EventCard';
import { localIso, getSchoolHolidays, findNextHoliday } from '../lib/schoolHolidays';

const CATS = [
  { value: '', label: 'All' },
  { value: 'festival', label: 'Festivals' },
  { value: 'market', label: 'Markets' },
  { value: 'family', label: 'Family' },
  { value: 'music', label: 'Music' },
  { value: 'sport', label: 'Sport' },
];

export default function EventsPage() {
  useSeo(
    "What's On in Leicester & Leicestershire — Event Listings | GoLeicestershire",
    "Visiting Leicester or Leicestershire? Up-to-date event listings across the city and county: festivals, family fun, heritage days and more. Filter by town, date or search — including this weekend and school holidays."
  );
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState('');
  const [town, setTown] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(params.get('from') || '');
  const [toDate, setToDate] = useState(params.get('to') || '');
  const [townOptions, setTownOptions] = useState([]);
  const [forecastMap, setForecastMap] = useState({});
  const [schoolHols, setSchoolHols] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => { getSchoolHolidays().then(setSchoolHols); }, []);

  // Month quick-links — counts come from the server so months beyond the first page still show
  const [months, setMonths] = useState([]);
  useEffect(() => {
    api.get('/events/months').then((r) => setMonths(r.data?.items || [])).catch(() => {});
  }, []);

  // 7-day forecast → per-date map so each card can show the day's outlook
  useEffect(() => {
    api.get('/weather').then((r) => {
      setForecastMap(Object.fromEntries((r.data?.forecast || []).map((f) => [f.date, f])));
    }).catch(() => {});
  }, []);

  // Load the town dropdown once — shared between /explore and /events
  useEffect(() => {
    api.get('/attractions/towns/list').then((r) => setTownOptions(r.data?.items || [])).catch(() => {});
  }, []);

  // Debounce the text search so we don't fire on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const buildQuery = (skip) => {
    const q = new URLSearchParams();
    if (cat) q.set('category', cat);
    if (town) q.set('town', town);
    if (debouncedSearch) q.set('search', debouncedSearch);
    if (fromDate) q.set('from_date', fromDate);
    if (toDate) q.set('to_date', toDate);
    q.set('limit', '120');
    if (skip) q.set('skip', String(skip));
    return q.toString();
  };

  useEffect(() => {
    api.get(`/events?${buildQuery(0)}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total ?? (r.data.items || []).length);
    });
  }, [cat, town, debouncedSearch, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadMore() {
    setLoadingMore(true);
    api.get(`/events?${buildQuery(items.length)}`)
      .then((r) => {
        setItems((prev) => [...prev, ...(r.data.items || [])]);
        setTotal(r.data.total ?? total);
      })
      .finally(() => setLoadingMore(false));
  }

  const filtersActive = cat || town || search || fromDate || toDate;
  function clearAll() { setCat(''); setTown(''); setSearch(''); setFromDate(''); setToDate(''); }

  // Quick date presets — local-date safe (no UTC off-by-one late at night)
  const presets = useMemo(() => {
    const now = new Date();
    const day = now.getDay(); // 0 Sun … 6 Sat
    let wkFrom, wkTo;
    if (day === 0) { wkFrom = now; wkTo = now; } // Sunday: what's left of the weekend
    else {
      wkFrom = new Date(now); wkFrom.setDate(now.getDate() + (6 - day));
      wkTo = new Date(wkFrom); wkTo.setDate(wkFrom.getDate() + 1);
      if (day === 6) wkFrom = now; // Saturday: start today
    }
    const week = new Date(now); week.setDate(now.getDate() + 7);
    const todayIso = localIso(now);
    const hol = findNextHoliday(schoolHols, now);
    const list = [
      { key: 'weekend', label: 'This weekend', from: localIso(wkFrom), to: localIso(wkTo) },
      { key: 'next7', label: 'Next 7 days', from: localIso(now), to: localIso(week) },
    ];
    if (hol) list.push({ key: 'school', label: hol.label || 'School holidays', from: hol.from > todayIso ? hol.from : todayIso, to: hol.to });
    return list;
  }, [schoolHols]); // eslint-disable-line react-hooks/exhaustive-deps

  function applyPreset(p) {
    if (fromDate === p.from && toDate === p.to) { setFromDate(''); setToDate(''); } // toggle off
    else { setFromDate(p.from); setToDate(p.to); }
  }

  // "Jump to month" — sets the date range to that calendar month (toggles off on re-click)
  function monthRange(m) {
    const [y, mo] = m.split('-').map(Number);
    const last = new Date(y, mo, 0).getDate();
    return { from: `${m}-01`, to: `${m}-${String(last).padStart(2, '0')}` };
  }
  function applyMonth(m) {
    const { from, to } = monthRange(m);
    if (fromDate === from && toDate === to) { setFromDate(''); setToDate(''); }
    else { setFromDate(from); setToDate(to); }
  }
  function monthLabel(m) {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
  }

  const groups = useMemo(() => items.reduce((acc, e) => {
    // ongoing events (started earlier, still running) group under the CURRENT month
    const now = new Date();
    const start = new Date(e.start_date);
    const end = e.end_date ? new Date(e.end_date) : start;
    const groupDate = start < now && end >= now ? now : start;
    const k = groupDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    (acc[k] = acc[k] || []).push(e);
    return acc;
  }, {}), [items]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      {items.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: "What's On in Leicester & Leicestershire",
            itemListElement: items.slice(0, 10).map((e, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: buildEventSchema(e),
            })),
          })}</script>
        </Helmet>
      )}
      <div className="label text-heritage mb-3">What&apos;s on in Leicester &amp; Leicestershire</div>
      <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] mb-10">What&apos;s on: <em className="not-italic text-heritage">your calendar, made fun.</em></h1>

      {/* Trade partner event submission note */}
      <div className="bg-forest/5 border border-forest/20 rounded-2xl p-5 mb-10 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="events-trade-note">
        <p className="text-sm text-charcoal/80 flex-1">
          <strong className="text-forest">Organise events in Leicestershire?</strong>{' '}
          Event listings here are curated by our team and submitted by our Trade Partners.
          To put your own events forward for the What&apos;s On calendar, create a free trade
          account — once approved, you can submit events straight from your partner dashboard.
        </p>
        <Link to="/partners" data-testid="events-trade-note-link"
          className="shrink-0 bg-forest text-base text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-forest/90 whitespace-nowrap">
          Create a trade account →
        </Link>
      </div>

      {/* Filter + search panel */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 mb-12" data-testid="events-filter-panel">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <label className="block">
            <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider block mb-1">Search</span>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40"/>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Event name or keyword"
                className="w-full bg-white border border-charcoal/15 rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-heritage"
                data-testid="events-search-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider block mb-1">Town</span>
            <select
              value={town}
              onChange={(e) => setTown(e.target.value)}
              className="w-full bg-white border border-charcoal/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-heritage"
              data-testid="events-town-select"
            >
              <option value="">Anywhere in Leicestershire</option>
              {townOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider block mb-1">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full bg-white border border-charcoal/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-heritage"
              data-testid="events-from-date"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider block mb-1">To</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
              className="w-full bg-white border border-charcoal/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-heritage"
              data-testid="events-to-date"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider mr-1">When:</span>
          {presets.map((p) => {
            const active = fromDate === p.from && toDate === p.to;
            return (
              <button key={p.key} onClick={() => applyPreset(p)} data-testid={`events-preset-${p.key}`}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? 'bg-heritage text-base border border-heritage' : 'bg-white border border-charcoal/15 hover:border-heritage'}`}>
                {p.label}
              </button>
            );
          })}
        </div>
        {months.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="events-month-links">
            <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider mr-1">Jump to month:</span>
            {months.map((m) => {
              const { from, to } = monthRange(m.month);
              const active = fromDate === from && toDate === to;
              return (
                <button key={m.month} onClick={() => applyMonth(m.month)} data-testid={`events-month-${m.month}`}
                  title={`${m.count} event${m.count === 1 ? '' : 's'}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? 'bg-heritage text-base border border-heritage' : 'bg-white border border-charcoal/15 hover:border-heritage'}`}>
                  {monthLabel(m.month)} <span className={active ? 'opacity-80' : 'text-charcoal/40'}>({m.count})</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-charcoal/60 uppercase tracking-wider mr-1">Type:</span>
          {CATS.map((c) => (
            <button key={c.value} onClick={() => setCat(c.value)} data-testid={`events-cat-${c.value || 'all'}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${cat === c.value ? 'bg-forest text-base border border-forest' : 'bg-white border border-charcoal/15 hover:border-forest'}`}>
              {c.label}
            </button>
          ))}
          {filtersActive && (
            <button onClick={clearAll} className="text-sm text-muted hover:text-heritage inline-flex items-center gap-1 ml-auto" data-testid="events-filters-clear">
              <X size={13}/> Clear filters
            </button>
          )}
        </div>
      </div>

      {items.length === 0 && <div className="text-center py-20 text-muted" data-testid="events-empty">No upcoming events match those filters. Try clearing some or widening the dates.</div>}

      {Object.entries(groups).map(([month, evs]) => (
        <section key={month} className="mb-16">
          <div className="label text-charcoal/60 mb-6">{month}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {evs.map((e) => (<EventCard key={e.id} event={e} forecast={forecastMap[(e.start_date || '').slice(0, 10)]} />))}
          </div>
        </section>
      ))}

      {items.length > 0 && (
        <div className="text-center pb-8" data-testid="events-count-note">
          <p className="text-sm text-muted mb-4">Showing {items.length} of {total} events</p>
          {items.length < total && (
            <button onClick={loadMore} disabled={loadingMore} data-testid="events-load-more"
              className="bg-heritage text-base font-semibold px-8 py-3 rounded-full hover:bg-heritage/90 disabled:opacity-60 transition-colors">
              {loadingMore ? 'Loading…' : `Load more events (${total - items.length} more)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
